import React from 'react';
import {
  Row,
  Col,
  Popconfirm,
  Switch,
  Select,
  Input,
  Checkbox,
  Tooltip,
  Button,
} from 'antd';
import styles from './custom-feed.module.sass';
import Icon, { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import {
  FEED_IMPORTANCE_OPTIONS,
  FEED_LABEL_OPTIONS,
} from '../../../../redux/constants/feed';
import TagSelect from '../../../../shared-components/TagSelect';

const GlobalFeedIconSVG = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 0C5.376 0 0 5.376 0 12C0 18.624 5.376 24 12 24C18.624 24 24 18.624 24 12C24 5.376 18.624 0 12 0ZM10.8 21.516C6.06 20.928 2.4 16.896 2.4 12C2.4 11.256 2.496 10.548 2.652 9.852L8.4 15.6V16.8C8.4 18.12 9.48 19.2 10.8 19.2V21.516ZM19.08 18.468C18.768 17.496 17.88 16.8 16.8 16.8H15.6V13.2C15.6 12.54 15.06 12 14.4 12H7.2V9.6H9.6C10.26 9.6 10.8 9.06 10.8 8.4V6H13.2C14.52 6 15.6 4.92 15.6 3.6V3.108C19.116 4.536 21.6 7.98 21.6 12C21.6 14.496 20.64 16.764 19.08 18.468Z"
      fill="white"
      fillOpacity="0.65"
    />
  </svg>
);

const GlobalFeedIcon = (props) => (
  <Icon component={GlobalFeedIconSVG} {...props} />
);

export class FeedRow extends React.Component {
  state = {
    hasChanges: false,
    name: this.props.feed?.name,
    type: this.props.feed?.type,
    score: this.props.feed?.custom_score,
    importance: this.props.feed?.importance,
    trust_level: this.props.feed?.trust_level,
    overwrite_score: this.props.feed?.overwrite_score,
    is_active: this.props.feed?.is_active,
    tags: this.props.feed?.tags,
    cost_per_month: this.props.feed?.cost_per_month,
    threat_duration: this.props.feed?.threat_duration,
    currentFeedName: null,
    currentFeedType: this.props.feed?.type,
    correlation: this.props.feed?.correlation_score,
    contribution: this.props.feed?.contribution_score,
  };

  handleScoreChange = (e) => {
    let { value } = e.target;
    value = parseInt(value) || 0;

    if (value < 0 || value > 100) return;
    this.setState({
      hasChanges: true,
      score: value,
    });
  };

  handleCostPerMonthChange = (e) => {
    let { value } = e.target;
    value = parseInt(value) || 0;

    if (value < 0 || value > 1000) return;
    this.setState({
      hasChanges: true,
      cost_per_month: value,
    });
  };

  handleThreatDurationChange = (e) => {
    let { value } = e.target;
    value = parseInt(value) || 0;

    if (value < 0 || value > 1000) return;
    this.setState({
      hasChanges: true,
      threat_duration: value,
    });
  };

  handleSaveClick = () => {
    const { feed } = this.props;
    const {
      name,
      score,
      importance,
      trust_level,
      overwrite_score,
      is_active,
      threat_duration,
      cost_per_month,
      currentFeedType,
      currentFeedName,
      tags,
    } = this.state;

    if (currentFeedName) {
      this.props.createFeed({
        is_user_created: true,
        name: currentFeedName ?? 'myFeed',
        type: currentFeedType ?? 'url',
        ...(score && {
          custom_score: score ?? 0,
        }),
        ...(importance && {
          importance: importance ?? 'low',
        }),
        cost_per_month: cost_per_month ?? 0,
        ...(threat_duration && {
          threat_duration: threat_duration ?? 0,
        }),
        tags,
        overwrite_score,
        is_active,
        trust_level,
      });

      this.setState({
        hasChanges: false,
      });

      this.props.deleteEmptyFeed();
    } else if (feed.uuid) {
      this.props.updateFeed({
        uuid: feed.uuid,
        name: name ?? 'myFeed',
        overwrite_score,
        custom_score: score ?? 0,
        cost_per_month: cost_per_month ?? 0,
        ...(threat_duration && {
          threat_duration: threat_duration ?? 0,
        }),
        is_active,
        tags: tags[0] ?? [],
        score: overwrite_score ? score : feed.score,
        importance: overwrite_score ? feed.importance : importance,
        trust_level,
      });

      this.setState({
        score: overwrite_score ? score : feed.score,
        importance: overwrite_score ? feed.importance : importance,
        hasChanges: false,
      });
    }
  };

  handleCancelClick = () => {
    const { feed } = this.props;
    this.setState({
      name: feed?.name,
      score: feed?.custom_score,
      importance: feed?.importance,
      trust_level: feed?.trust_level,
      tags: feed?.tags,
      overwrite_score: feed?.overwrite_score,
      is_active: feed?.is_active,
      cost_per_month: feed?.cost_per_month,
      threat_duration: feed?.threat_duration,
      simpleAddSelectorValue: null,
      hasChanges: false,
      currentFeedName: null,
      currentFeedType: feed?.type,
    });
  };

  handleDeleteClick = (uuid) => {
    if (uuid) {
      this.props.deleteFeed(uuid);
    }
    if (!uuid) {
      this.props.deleteEmptyFeed();
    }
  };

  handleTagSelect = (tag) => {
    this.setState({
      hasChanges: true,
      tags: [tag],
    });
    this.props.resetTagSearch();
  };

  render() {
    const { feed } = this.props;
    const {
      hasChanges,
      name,
      type,
      score,
      importance,
      trust_level,
      overwrite_score,
      is_active,
      currentFeedName,
      currentFeedType,
      threat_duration,
      tags,
      correlation,
      contribution,
    } = this.state;
    const rowColor = is_active
      ? feed.is_user_created
        ? '#262a34'
        : '#182a30'
      : '#16262b';

    return (
      <Row
        style={{ width: '100%', backgroundColor: rowColor }}
        className={styles.row}
      >
        <Col span={1}>
          <div
            className={styles['action-buttons']}
            style={{ textAlign: 'left' }}
          >
            {feed.name &&
              (feed.is_user_created ? (
                <Tooltip title="Source created by users from your organization">
                  <UserOutlined />
                </Tooltip>
              ) : (
                <Tooltip title="Public source">
                  <GlobalFeedIcon />
                </Tooltip>
              ))}
            {!feed.name && <UserOutlined />}
          </div>
        </Col>

        {feed.name && (
          <Col span={3}>
            <Input
              placeholder="Feed name"
              value={name}
              onChange={(event) =>
                this.setState({
                  hasChanges: true,
                  name: event.target.value,
                })
              }
            ></Input>
          </Col>
        )}
        {!feed.name && (
          <Col span={3}>
            <Input
              placeholder="Feed name"
              value={currentFeedName}
              onChange={(event) =>
                this.setState({
                  hasChanges: true,
                  currentFeedName: event.target.value,
                })
              }
            ></Input>
          </Col>
        )}

        {feed.type && (
          <Col span={2}>{typeof type === 'string' && type.toUpperCase()} </Col>
        )}
        {!feed.type && (
          <Col span={2}>
            <Select
              //style={{ minWidth: 80, marginLeft: '10px', marginRight: '10px' }}
              placeholder="Type"
              defaultValue={currentFeedType}
              value={currentFeedType}
              onChange={(currentFeedType) =>
                this.setState({
                  hasChanges: true,
                  currentFeedType,
                })
              }
            >
              <Select.Option value="ip">IP</Select.Option>
              <Select.Option value="domain">Domain</Select.Option>
              <Select.Option value="url">URL</Select.Option>
            </Select>
          </Col>
        )}

        <Col span={1}>
          <Switch
            //style={{ marginRight: 10, marginLeft: 10 }}
            checked={is_active}
            onChange={() =>
              this.setState({
                hasChanges: true,
                is_active: !is_active,
              })
            }
          />
        </Col>
        <Col span={3}>
          <Select
            style={{ maxWidth: 140 }}
            defaultActiveFirstOption
            options={FEED_LABEL_OPTIONS}
            defaultValue={trust_level}
            value={trust_level}
            onChange={(trust_level) =>
              this.setState({
                hasChanges: true,
                trust_level,
              })
            }
          />
        </Col>

        <Col span={3}>
          <TagSelect
            //style={{ minWidth: 150, marginLeft: '10px', marginRight: '10px' }}
            foundTags={this.props.foundTags}
            searchStatus={this.props.searchStatus}
            searchForTags={this.props.searchForTags}
            value={tags?.[0]?.name}
            onSelect={this.handleTagSelect}
            onChange={(tag) =>
              this.setState({
                hasChanges: true,
                tags: tags?.[0]?.name,
              })
            }
          />
        </Col>
        <Col span={2}>
          <Select
            disabled={overwrite_score}
            //style={{ minWidth: 100 }}
            defaultActiveFirstOption
            options={FEED_IMPORTANCE_OPTIONS}
            value={importance}
            onChange={(importance) =>
              this.setState({
                hasChanges: true,
                importance,
              })
            }
          />
        </Col>
        <Col span={2}>
          <Checkbox
            style={{ marginRight: '5px' }}
            checked={overwrite_score}
            onChange={() =>
              this.setState({
                hasChanges: true,
                overwrite_score: !overwrite_score,
              })
            }
          />
          <Input
            disabled={!overwrite_score}
            style={{ width: '50px' }}
            defaultValue={score}
            value={score}
            onChange={this.handleScoreChange}
          />
        </Col>
        {/* <Col span={1}>
          <Input
            suffix="$"
            className={styles.price_input}
            //style={{ width: '65px' }}
            defaultValue={cost_per_month}
            value={cost_per_month}
            onChange={this.handleCostPerMonthChange}
          />
        </Col> */}
        <Col span={1}>
          <Input
            style={{ width: '50px' }}
            defaultValue={threat_duration}
            value={threat_duration}
            onChange={this.handleThreatDurationChange}
          />
        </Col>
        <Col span={2}>
          {/* <Input
            disabled
            style={{ width: '50px' }}
            defaultValue={contribution}
            value={contribution}
            //onChange={this.handleThreatDurationChange}
          /> */}
          <Tooltip title="Percentage of items overlapping with other Feeds/Collections">
            {contribution} %
          </Tooltip>
        </Col>
        <Col span={1}>
          {/* <Input
            disabled
            style={{ width: '50px' }}
            defaultValue={correlation}
            value={correlation}
            //onChange={this.handleThreatDurationChange}
          /> */}
          <Tooltip title="Percentage of firstly reported items since the Feed/Collection was added">
            {correlation} %
          </Tooltip>
        </Col>
        <Col span={3}>
          <div className={styles['action-buttons']}>
            {!hasChanges && (
              <Popconfirm
                title={<div style={{ color: '#ccd4d9' }}>Are you sure?</div>}
                style={{ color: '#ccd4d9' }}
                onConfirm={() => this.handleDeleteClick(feed.uuid)}
              >
                <Tooltip title="Remove source from the list">
                  <DeleteOutlined data-testid={`delete-${feed.uuid}`} />
                </Tooltip>
              </Popconfirm>
            )}
          </div>
          {hasChanges && (
            <>
              <Button
                className={styles.save_button}
                onClick={this.handleSaveClick}
              >
                Save
              </Button>
              <Button
                className={styles.cancel_button}
                onClick={this.handleCancelClick}
              >
                Cancel
              </Button>
            </>
          )}
        </Col>
      </Row>
    );
  }
}

export default FeedRow;
